import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import { BoolCell, DateCell, MutedCell, NameCell, NumberCell } from '@/components/TableCells'
import { AlertCircle, User, UserPlus, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AuthService } from '@/services/core/auth'
import { StorageService } from '@/services/core/storage'
import { UsersApi, NetworkUser } from '@/services/users/usersApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { AppShell } from '../../components/AppShell'
import { BulkActionBar } from '../../components/BulkActionBar'
import { CardContent, CardIcon } from '@/components/ui/card'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { NewUserModal } from '../../components/NewUserModal'
import { MasqueradeLink } from '../../components/MasqueradeLink'
import { useCurrentUser } from '../../contexts/UserContext'
import { TeamsApi } from '@/services/users/teamsApi'
import { PoliticalEntitiesApi } from '@/services/political/politicalEntitiesApi'
import { formatDate, formatTime } from '../../utils/format'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useNetwork } from '@/contexts/NetworkContext'
import type { Network } from '@/services/networks/networksApi'
import type { ListSort } from '@/services/core/paging'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'

/** Usernames the API hides from this user, e.g. `j***n`, cannot be signed in as. */
const isObfuscated = (username: string) => !username.includes('@') && username.includes('*')

/** Teams listed in a row before the rest are summed up as "+N more". */
const MAX_TEAMS_SHOWN = 3

/** The API field each sortable column orders by — the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  displayName: 'displayName',
  username: 'username',
  objectState: 'objectState',
  lastLogin: 'lastLogin',
  locale: 'locale',
  timeZone: 'timezone',
  loginEnabled: 'loginEnabled',
  login2faRequired: 'login2faRequired',
  lastPasswordChange: 'lastPasswordChange',
  updateTime: 'updateTime',
  createTime: 'createTime',
}

/** The user's roles in the current network only. */
function rolesHere(user: NetworkUser, network: Network | null) {
  return (user.userRoles?.rolesByNetwork ?? [])
    .filter((nr) => !network || nr.network.tagId === network.tagId || nr.network.id === network.id)
    .flatMap((nr) => nr.roles)
}

const ROLE_STYLES: Record<string, { bg: string; color: string }> = {
  'Political Controller': { bg: 'rgba(47,109,244,0.1)', color: '#2f6df4' },
  'Political Sponsor': { bg: 'rgba(22,163,74,0.1)', color: '#16a34a' },
  'Read-Only Political': { bg: 'rgba(234,88,12,0.1)', color: '#ea580c' },
  'System Admin': { bg: 'rgba(220,38,38,0.1)', color: '#dc2626' },
}

function RoleBadge({ name }: { name: string }) {
  const s = ROLE_STYLES[name] ?? { bg: 'var(--ad-neutral-soft)', color: 'var(--ad-text-muted)' }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.03em',
        background: s.bg,
        color: s.color,
      }}
    >
      {name}
    </span>
  )
}

function StatusBadge({ user }: { user: NetworkUser }) {
  const { t } = useTranslation()
  const active = user.objectState === 'ACTIVE' && user.loginEnabled
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        background: active ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)',
        color: active ? '#16a34a' : '#dc2626',
      }}
    >
      {active ? t('common.active') : t('common.inactive')}
    </span>
  )
}

function RolesCell({ user, network }: { user: NetworkUser; network: Network | null }) {
  const roleNames = new Set<string>()
  for (const r of rolesHere(user, network)) {
    if (r.role?.name && r.role.name !== 'Political Network Member Role') {
      roleNames.add(r.role.name)
    }
  }
  if (roleNames.size === 0) return <span style={{ color: 'var(--ad-text-faint)' }}>—</span>
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {[...roleNames].map((name) => (
        <RoleBadge key={name} name={name} />
      ))}
    </div>
  )
}

function TeamsCell({ user, network }: { user: NetworkUser; network: Network | null }) {
  const { t } = useTranslation()
  const teamNames = new Set<string>()
  for (const r of rolesHere(user, network)) {
    if (r.team?.name) teamNames.add(r.team.name)
  }
  if (teamNames.size === 0) return <span style={{ color: 'var(--ad-text-faint)' }}>—</span>
  const names = [...teamNames]
  const hidden = names.length - MAX_TEAMS_SHOWN
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {names.slice(0, MAX_TEAMS_SHOWN).map((name) => (
        <span key={name} style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 5 }}>
          <Users className="size-3" style={{ color: 'var(--ad-text-muted)' }} />
          {name}
        </span>
      ))}
      {hidden > 0 && (
        <span className="text-xs text-(--ad-text-muted)">{t('users.moreTeams', { count: hidden })}</span>
      )}
    </div>
  )
}

function LastLoginCell({ user }: { user: NetworkUser }) {
  if (!user.lastLogin) return <span style={{ color: 'var(--ad-text-faint)' }}>Never</span>
  const date = new Date(user.lastLogin)
  return (
    <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>
      {formatDate(date)} {formatTime(date)}
    </span>
  )
}

interface UsersPageProps {
  basePath: string
}

export function UsersPage({ basePath }: UsersPageProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const hasToken = AuthService.isAuthenticated()
  const queryClient = useQueryClient()
  const [newUserOpen, setNewUserOpen] = useState(false)
  const [search, setSearch] = useUrlSearch()
  const [selection, setSelection] = useState<NetworkUser[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>(
    'filters.users.status',
    'ACTIVE'
  )
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const { currentNetwork, scopeKey, scopeReady } = useNetwork()

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const currentUser = useCurrentUser()
  // Support staff already masquerading can open another masquerade from here,
  // as in the old UI.
  const masquerade = StorageService.getMasquerade()
  // New users are created as political sponsors, so creation is only offered in the political module.
  const isPolitical = basePath === '/political/users'
  const { data: entities = [] } = useQuery({
    queryKey: ['politicalEntities'],
    queryFn: () => PoliticalEntitiesApi.getAll(),
    enabled: hasToken && isPolitical,
    staleTime: 5 * 60 * 1000,
  })
  const { data: allTeams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })

  // Determine which sponsor teams are available for user creation.
  // Sponsor entities have ownerTeamId pointing to their team.
  const sponsorTeamIds = new Set(
    entities.filter((e) => e.type === 'SPONSOR' && e.ownerTeamId).map((e) => e.ownerTeamId!)
  )
  const sponsorTeams = allTeams.filter((t) => sponsorTeamIds.has(t.id))

  // Both controllers and sponsors can create users; the allowed teams differ by role
  // but are already correctly scoped via the entities visible to the current user.
  const canCreateUsers = isPolitical && !!currentUser && sponsorTeams.length > 0

  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['users', scopeKey, search, statusFilter, page, pageSize, sort],
    queryFn: () => UsersApi.getPage({ page, pageSize, search, status: statusFilter, sort }),
    enabled: hasToken && scopeReady,
  })

  const users = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(
        selection.map((u) => UsersApi.update(u.id, { objectState: state, loginEnabled: state === 'ACTIVE' }))
      )
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success(t('common.done'), {
        description: t(state === 'ACTIVE' ? 'users.doneDetail_activate' : 'users.doneDetail_deactivate', { count: selection.length }),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  const columns: ColumnDef<NetworkUser, unknown>[] = [
    selectionColumn<NetworkUser>(),
    {
      accessorKey: 'displayName',
      header: t('common.name'),
      cell: ({ row }) => <NameCell name={row.original.displayName} id={row.original.id} />,
      meta: { minWidth: 200 },
    },
    {
      accessorKey: 'username',
      header: t('users.username'),
      cell: ({ row }) => {
        const { username } = row.original
        return (
          <span className="inline-flex items-center gap-1.5">
            <span style={{ fontSize: 12.5, color: 'var(--ad-text-muted)' }}>{username}</span>
            {masquerade && username !== currentUser?.username && !isObfuscated(username) && (
              <MasqueradeLink username={username} supportUsername={masquerade.supportUsername} />
            )}
          </span>
        )
      },
      meta: { minWidth: 200 },
    },
    {
      id: 'roles',
      header: t('users.roles'),
      cell: ({ row }) => <RolesCell user={row.original} network={currentNetwork} />,
      enableSorting: false,
      meta: { minWidth: 180 },
    },
    {
      id: 'teams',
      header: t('common.teams'),
      cell: ({ row }) => <TeamsCell user={row.original} network={currentNetwork} />,
      enableSorting: false,
      meta: { minWidth: 180 },
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      cell: ({ row }) => <StatusBadge user={row.original} />,
      size: 100,
    },
    {
      accessorKey: 'lastLogin',
      header: t('users.lastLogin'),
      cell: ({ row }) => <LastLoginCell user={row.original} />,
      meta: { minWidth: 150 },
    },
    optionalColumn<NetworkUser>({
      accessorKey: 'locale',
      header: t('users.locale'),
      size: 110,
      cell: ({ row }) => <MutedCell value={row.original.locale} />,
    }),
    optionalColumn<NetworkUser>({
      accessorKey: 'timeZone',
      header: t('users.timeZone'),
      meta: { minWidth: 150 },
      cell: ({ row }) => <MutedCell value={row.original.timeZone} />,
    }),
    optionalColumn<NetworkUser>({
      accessorKey: 'loginEnabled',
      header: t('users.loginEnabled'),
      size: 130,
      cell: ({ row }) => <BoolCell value={row.original.loginEnabled} />,
    }),
    optionalColumn<NetworkUser>({
      accessorKey: 'login2faRequired',
      header: t('users.twoFactor'),
      size: 130,
      cell: ({ row }) => <BoolCell value={row.original.login2faRequired} />,
    }),
    optionalColumn<NetworkUser>({
      accessorKey: 'lastPasswordChange',
      header: t('users.lastPasswordChange'),
      meta: { minWidth: 160 },
      cell: ({ row }) => <DateCell value={row.original.lastPasswordChange} />,
    }),
    optionalColumn<NetworkUser>({
      accessorKey: 'passwordExpiresDays',
      header: t('users.passwordExpiry'),
      size: 140,
      cell: ({ row }) => <NumberCell value={row.original.passwordExpiresDays ?? null} />,
    }),
    optionalColumn<NetworkUser>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<NetworkUser>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <AppShell crumbs={[{ label: t('nav.users') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('nav.users')}</h1>
            <p>{t('users.subtitle')}</p>
          </div>
          {(canCreateUsers || !isPolitical) && (
            <div className="ad-page__actions">
              <Button
                onClick={() =>
                  isPolitical
                    ? setNewUserOpen(true)
                    : navigate(
                        `${basePath}/${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}?new=true`
                      )
                }
              >
                <UserPlus className="size-4" />
                {t('users.newUser')}
              </Button>
            </div>
          )}
        </div>

        {error && (
          <div className="auth-form__error" style={{ marginBottom: 16 }}>
            <AlertCircle className="size-4" />
            {(error as Error).message}
          </div>
        )}

        <ListCard tone="people">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={User} />
                {t('nav.users')}
              </>
            }
            count={totalCount}
            actions={
              <PageSizeSelect
                value={pageSize}
                onChange={(size) => refilter(() => setPageSize(size))}
              />
            }
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton
                  value={statusFilter}
                  onChange={(v) => refilter(() => setStatusFilter(v))}
                />
              </>
            }
          />

          <CardContent className="p-0">
            <BulkActionBar
              count={selection.length}
              onClear={() => setSelection([])}
              loading={bulkLoading}
              onActivate={() => bulkSetStatus('ACTIVE')}
              onDeactivate={() => bulkSetStatus('INACTIVE')}
            />
            <DataTable
              data={users}
              columns={columns}
              loading={isLoading}
              emptyMessage={
                hasToken
                  ? t('users.noUsersFound')
                  : t('common.signInToLoad')
              }
              rowHref={(row) => `${basePath}/${row.id}`}
              columnsKey="users"
              selection={selection}
              onSelectionChange={setSelection}
              serverSort={{ sort, onChange: handleSort, fields: SORT_FIELDS }}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('users.userCount', { count: totalCount })}
          />
        </ListCard>
      </div>

      <NewUserModal
        open={newUserOpen}
        onClose={() => setNewUserOpen(false)}
        onCreated={() => setNewUserOpen(false)}
        allowedTeams={sponsorTeams}
      />
    </AppShell>
  )
}
